import { useCallback, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { SearchableSelect } from '@/components/common/searchable-select';
import { Button } from '@/components/ui/button';
import { DatePicker } from '@/components/ui/date-picker';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { toast } from '@/components/ui/toast';
import {
  collectVersions,
  type OnFarmImplantPregCheckRow,
  OnFarmImplantRequestError,
  useRecordOnFarmImplantPregnancyLoss,
} from '@/services/on-farm-implant';
import { toWallClockDate } from '@/utils';

import { useCheckedByOptions } from './useCheckedByOptions';

type PregnancyLossDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  sessionId: string;
  rows: OnFarmImplantPregCheckRow[];
  onSaved: () => void;
};

export const PregnancyLossDialog = ({
  open,
  onOpenChange,
  sessionId,
  rows,
  onSaved,
}: PregnancyLossDialogProps) => {
  const { t } = useTranslation();
  const { techOptions, defaultCheckedById } = useCheckedByOptions();

  const [lossDate, setLossDate] = useState<Date | undefined>(() => new Date());
  const [checkedById, setCheckedById] = useState<string | null>(null);
  const [problems, setProblems] = useState<string[]>([]);

  const { mutateAsync: recordLoss, isPending } =
    useRecordOnFarmImplantPregnancyLoss();

  const checkedByValue = checkedById ?? defaultCheckedById;
  const missing = !lossDate || checkedByValue === '';
  const canSave = rows.length > 0 && !missing && !isPending;

  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (!nextOpen) {
        setLossDate(new Date());
        setCheckedById(null);
        setProblems([]);
      }
      onOpenChange(nextOpen);
    },
    [onOpenChange],
  );

  const handleSave = useCallback(async () => {
    if (!canSave || !lossDate) return;

    setProblems([]);
    try {
      const saved = await recordLoss({
        sessionId,
        input: {
          sessionQueueIds: rows.map((row) => row.sessionQueueId),
          lossDate: toWallClockDate(lossDate),
          checkedById: checkedByValue,
          versions: collectVersions(rows),
        },
      });

      onSaved();
      handleOpenChange(false);
      toast.add({
        data: { variant: 'success' },
        title: t('onFarmImplant.pregCheck.loss.success', {
          count: saved.length,
        }),
      });
    } catch (error) {
      if (error instanceof OnFarmImplantRequestError && error.problems.length) {
        setProblems(error.problems);
        return;
      }
      console.error('Failed to save pregnancy loss:', error);
      toast.add({
        data: { variant: 'error' },
        title: t('onFarmImplant.pregCheck.loss.failed'),
      });
    }
  }, [
    canSave,
    checkedByValue,
    handleOpenChange,
    lossDate,
    onSaved,
    recordLoss,
    rows,
    sessionId,
    t,
  ]);

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="md:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('onFarmImplant.pregCheck.loss.title')}</DialogTitle>
          <DialogDescription>
            {t('onFarmImplant.pregCheck.loss.description')}
          </DialogDescription>
        </DialogHeader>

        <DialogBody className="flex flex-col gap-3">
          {problems.length > 0 && (
            <div
              role="alert"
              className="rounded-md bg-red-50 p-3 text-xs text-red-700"
            >
              <ul className="list-inside list-disc">
                {problems.map((problem) => (
                  <li key={problem}>{problem}</li>
                ))}
              </ul>
            </div>
          )}

          <div className="grid grid-cols-[8rem_1fr] items-center gap-3">
            <Label className="text-xs" htmlFor="pregnancy-loss-date">
              {t('onFarmImplant.pregCheck.loss.lossDate')}
            </Label>
            <DatePicker
              id="pregnancy-loss-date"
              value={lossDate}
              onChange={setLossDate}
              placeholder={t('onFarmImplant.pregCheck.update.selectDate')}
              className="h-8 w-full text-xs"
            />

            <Label className="text-xs" htmlFor="pregnancy-loss-checked-by">
              {t('onFarmImplant.pregCheck.update.checkedBy')}
            </Label>
            <SearchableSelect
              className="h-8 w-full text-xs"
              options={techOptions}
              value={checkedByValue}
              onChange={(value) => setCheckedById(value)}
              placeholder={t('onFarmImplant.pregCheck.update.selectCheckedBy')}
              emptyMessage={t(
                'onFarmImplant.implantQueue.massChange.noOptions',
              )}
            />
          </div>

          {missing && (
            <p className="text-xs text-red-600">
              {t('onFarmImplant.pregCheck.loss.required')}
            </p>
          )}

          <p className="text-xs text-gray-500">
            {t('onFarmImplant.pregCheck.loss.hint')}
          </p>

          <p className="text-sm font-medium text-gray-700">
            {t('onFarmImplant.pregCheck.update.recordCount', {
              count: rows.length,
            })}
          </p>
        </DialogBody>

        <DialogFooter>
          <Button variant="secondary" onClick={() => handleOpenChange(false)}>
            {t('common.cancel')}
          </Button>
          <Button onClick={handleSave} disabled={!canSave}>
            {isPending && <i className="pi pi-spin pi-spinner" />}
            {t('onFarmImplant.pregCheck.loss.confirm')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
